import { useState } from 'react';
import type { KeyedMutator } from 'swr';
import type { IMfaAddTotpStartView, IMfaStatusView } from '@shared/services';
import { useStepUp } from './useStepUp';
import { addTotpStart, addTotpVerify } from '@/shared/api/mfa';
import { getLoginFlowError } from '@/shared/utils';

/**
 * The wizard position for in-page authenticator setup:
 * - `idle`   — no setup in progress; the card shows the `Set up` action.
 * - `stepup` — collecting the current password before starting enrollment.
 * - `setup`  — showing the QR / secret and accepting the authenticator code.
 * - `backup` — showing the one-time backup codes (shown exactly once).
 */
type TotpStep = 'idle' | 'stepup' | 'setup' | 'backup';

/**
 * Session-authorized authenticator (TOTP) setup, mirroring `usePasskeyManagement`: owns the wizard
 * step, the step-up gate, the setup material, the verify call, and the one-time backup codes.
 * Revalidates the shared `useMfaStatus` cache via `mutate` on success so the card flips to enabled
 * without a full reload. No disable path — the backend exposes no per-factor removal.
 */
export const useTotpSetup = (mutate: KeyedMutator<IMfaStatusView>) => {
  const stepUp = useStepUp();
  const [step, setStep] = useState<TotpStep>('idle');
  const [setupView, setSetupView] = useState<IMfaAddTotpStartView | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const open = () => setStep('stepup');

  const close = () => {
    setStep('idle');
    setSetupView(null);
    setBackupCodes([]);
    setCode('');
    setVerifyError(null);
    stepUp.reset();
  };

  // Step-up → start: a rejected password stays inline in the prompt (handled by `useStepUp`); a
  // successful start yields the setup material and advances to the QR / code step.
  const submitStepUp = async () => {
    const result = await stepUp.runStepUp((currentPassword) => addTotpStart({ currentPassword }));

    if (result.ok) {
      setSetupView(result.value);
      setStep('setup');
    }
  };

  // Verify the authenticator code. On success, surface the backup codes once; on failure keep the
  // step open with an inline error so the user can retry.
  const submitCode = async () => {
    setVerifyError(null);
    setIsVerifying(true);

    try {
      const { backupCodes: codes } = await addTotpVerify({ code });
      setBackupCodes(codes);
      setStep('backup');
    } catch (caught) {
      setVerifyError(getLoginFlowError(caught));
    } finally {
      setIsVerifying(false);
    }
  };

  // Acknowledge the backup codes: the factor is already enrolled, so revalidate and close.
  const finish = async () => {
    await mutate();
    close();
  };

  return {
    step,
    open,
    close,
    stepUp,
    submitStepUp,
    setupView,
    code,
    setCode,
    submitCode,
    isVerifying,
    verifyError,
    backupCodes,
    finish,
  };
};
